// plan-01 S04: localStorage save schema — bible S27

const STORAGE_KEY = 'vt.save'
const MAX_VISITS = 9999
const MAX_EVENTS = 99999
const MAX_LIST = 48
const MAX_PAINT_KEYS = 12
const MAX_LEAKS = 9
const MAX_RITUALS = 9
const MAX_PLAYER_ENTRIES = 48
const MAX_COMMUNE_LOG = 48

export interface VtSave {
  version: number
  visits: number
  events: number
  audioMuted: boolean
  postgradeOff: boolean
  reducedMotion: boolean
  divineSeen: string[]
  consensusSearched: string[]
  paintedAt: Record<string, number[]>
  pixel49Triggered: boolean
  palimpsestLeakSeen: number
  silenceRitualCompleted: number
  stageLast: string | null
  installPromptDismissed: number
  pwaInstalled: boolean
  polishSeenLogo: boolean
  firstSeenAt: number
  lastSeenAt: number
  broadcastsHeard: number
  lastBroadcastDate: string | null
  nodeAlias: string | null
  shellCwd: string | null
  playerState: Record<string, { lastPos: number; volume: number }>
  tapeState: { index: number; pos: number; volume: number } | null
  polishSeenPost: boolean
  polishBurnInPattern: string | null
  communeLog: Array<{ qHash: string; q: string; answerSeed: string; at: number }>
  communeSharedSeen: number
  cardExports: { heatmap: number; divine: number; commune: number }
  vagueifier: { blessings: number; loopsWoven: number; scries: number }
}

function fresh(): VtSave {
  const now = Date.now()
  return {
    version: 1,
    visits: 0,
    events: 0,
    audioMuted: false,
    postgradeOff: false,
    reducedMotion: false,
    divineSeen: [],
    consensusSearched: [],
    paintedAt: {},
    pixel49Triggered: false,
    palimpsestLeakSeen: 0,
    silenceRitualCompleted: 0,
    stageLast: null,
    installPromptDismissed: 0,
    pwaInstalled: false,
    polishSeenLogo: false,
    firstSeenAt: now,
    lastSeenAt: now,
    broadcastsHeard: 0,
    lastBroadcastDate: null,
    nodeAlias: null,
    shellCwd: null,
    playerState: {},
    tapeState: null,
    polishSeenPost: false,
    polishBurnInPattern: null,
    communeLog: [],
    communeSharedSeen: 0,
    cardExports: { heatmap: 0, divine: 0, commune: 0 },
    vagueifier: { blessings: 0, loopsWoven: 0, scries: 0 },
  }
}

function clampSave(s: VtSave): void {
  s.visits = Math.min(s.visits, MAX_VISITS)
  s.events = Math.min(s.events, MAX_EVENTS)
  if (s.divineSeen.length > MAX_LIST) {
    s.divineSeen = s.divineSeen.slice(-MAX_LIST)
  }
  if (s.consensusSearched.length > MAX_LIST) {
    s.consensusSearched = s.consensusSearched.slice(-MAX_LIST)
  }
  const paintKeys = Object.keys(s.paintedAt)
  if (paintKeys.length > MAX_PAINT_KEYS) {
    const keep = paintKeys.slice(-MAX_PAINT_KEYS)
    const next: Record<string, number[]> = {}
    for (const k of keep) next[k] = s.paintedAt[k]
    s.paintedAt = next
  }
  s.palimpsestLeakSeen = Math.min(s.palimpsestLeakSeen, MAX_LEAKS)
  s.silenceRitualCompleted = Math.min(s.silenceRitualCompleted, MAX_RITUALS)
  // plan-16: clamp playerState to MAX_PLAYER_ENTRIES (FIFO)
  const psKeys = Object.keys(s.playerState)
  if (psKeys.length > MAX_PLAYER_ENTRIES) {
    const keep = psKeys.slice(-MAX_PLAYER_ENTRIES)
    const next: Record<string, { lastPos: number; volume: number }> = {}
    for (const k of keep) next[k] = s.playerState[k]
    s.playerState = next
  }
  // plan-18: clamp communeLog to MAX_COMMUNE_LOG (FIFO)
  if (s.communeLog && s.communeLog.length > MAX_COMMUNE_LOG) {
    s.communeLog = s.communeLog.slice(-MAX_COMMUNE_LOG)
  }
}

let cache: VtSave | null = null

export function load(): VtSave {
  if (cache) return cache
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      cache = fresh()
      return cache
    }
    const parsed = JSON.parse(raw) as Partial<VtSave>
    const base = fresh()
    // merge: known fields from parsed, defaults from fresh for missing fields
    const merged: VtSave = {
      version: parsed.version ?? base.version,
      visits: parsed.visits ?? base.visits,
      events: parsed.events ?? base.events,
      audioMuted: parsed.audioMuted ?? base.audioMuted,
      postgradeOff: parsed.postgradeOff ?? base.postgradeOff,
      reducedMotion: parsed.reducedMotion ?? base.reducedMotion,
      divineSeen: parsed.divineSeen ?? base.divineSeen,
      consensusSearched: parsed.consensusSearched ?? base.consensusSearched,
      paintedAt: parsed.paintedAt ?? base.paintedAt,
      pixel49Triggered: parsed.pixel49Triggered ?? base.pixel49Triggered,
      palimpsestLeakSeen: parsed.palimpsestLeakSeen ?? base.palimpsestLeakSeen,
      silenceRitualCompleted: parsed.silenceRitualCompleted ?? base.silenceRitualCompleted,
      stageLast: parsed.stageLast ?? base.stageLast,
      installPromptDismissed: parsed.installPromptDismissed ?? base.installPromptDismissed,
      pwaInstalled: parsed.pwaInstalled ?? base.pwaInstalled,
      polishSeenLogo: parsed.polishSeenLogo ?? base.polishSeenLogo,
      firstSeenAt: parsed.firstSeenAt ?? base.firstSeenAt,
      lastSeenAt: parsed.lastSeenAt ?? base.lastSeenAt,
      broadcastsHeard: parsed.broadcastsHeard ?? base.broadcastsHeard,
      lastBroadcastDate: parsed.lastBroadcastDate ?? base.lastBroadcastDate,
      nodeAlias: parsed.nodeAlias ?? base.nodeAlias,
      shellCwd: parsed.shellCwd ?? base.shellCwd,
      playerState: parsed.playerState ?? base.playerState,
      tapeState: parsed.tapeState ?? base.tapeState,
      polishSeenPost: parsed.polishSeenPost ?? base.polishSeenPost,
      polishBurnInPattern: parsed.polishBurnInPattern ?? base.polishBurnInPattern,
      communeLog: parsed.communeLog ?? base.communeLog,
      communeSharedSeen: parsed.communeSharedSeen ?? base.communeSharedSeen,
      cardExports: {
        heatmap: parsed.cardExports?.heatmap ?? base.cardExports.heatmap,
        divine: parsed.cardExports?.divine ?? base.cardExports.divine,
        commune: parsed.cardExports?.commune ?? base.cardExports.commune,
      },
      vagueifier: {
        blessings: parsed.vagueifier?.blessings ?? base.vagueifier.blessings,
        loopsWoven: parsed.vagueifier?.loopsWoven ?? base.vagueifier.loopsWoven,
        scries: parsed.vagueifier?.scries ?? base.vagueifier.scries,
      },
    }
    clampSave(merged)
    cache = merged
    return cache
  } catch {
    console.log('save corrupted. reseeded. probably.')
    cache = fresh()
    return cache
  }
}

export function save(s: VtSave): void {
  clampSave(s)
  cache = s
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
}

export function bump(field: 'visits' | 'events', by: number = 1): void {
  const s = load()
  s[field] = Math.min(s[field] + by, field === 'visits' ? MAX_VISITS : MAX_EVENTS)
  save(s)

  // plan-09 S01: fire pixel-49 flicker when events hits exactly 48
  if (field === 'events' && s.events === 48) {
    import('../eggs/pixel49').then(({ checkEvents48 }) => {
      checkEvents48(s.events)
    }).catch(() => {})
  }

  // plan-09 S02: fire palimpsest Type-1 leak at multiples of 48
  if (field === 'events' && s.events > 0 && s.events % 48 === 0) {
    import('../eggs/palimpsest').then(({ triggerType1 }) => {
      triggerType1()
    }).catch(() => {})
  }
}

export function set(field: keyof VtSave, value: unknown): void {
  const s = load()
  ;(s as unknown as Record<string, unknown>)[field] = value
  save(s)
}
